import { bigIntParser } from "../utils/functions/bigIntParser";
import { endpointGenerator } from "../utils/functions/endpointGenerator";
import { type Execution } from "../utils/functions/execution";
import { paginateEndpoint } from "../utils/functions/paginateEndpoint";
import {
  type Chain,
  type GoldRushResponse,
} from "../utils/types/Generic.types";
import {
  type ChainCollectionResponse,
  type CheckOwnershipInNftQueryParamOpts,
  type GetChainCollectionsQueryParamOpts,
  type GetCollectionMarketDataQueryParamOpts,
  type GetNftMetadataForGivenTokenIdForContractQueryParamOpts,
  type GetNftTransactionsForContractTokenIdQueryParamOpts,
  type GetNftsForAddressQueryParamOpts,
  type GetTokenIdsForContractWithMetadataQueryParamOpts,
  type NftAddressBalanceNftResponse,
  type NftCollectionAttributesForTraitResponse,
  type NftCollectionFloorPriceResponse,
  type NftCollectionSalesCountResponse,
  type NftCollectionTraitsResponse,
  type NftCollectionTraitsSummaryResponse,
  type NftCollectionVolumeResponse,
  type NftMetadataResponse,
  type NftOwnershipForCollectionResponse,
  type NftTransactionsResponse,
} from "../utils/types/NftService.types";

/**
 * NFTs API
 *
 */
export class NftService {
  constructor(private execution: Execution) {}

  /**
   *
   * Commonly used to fetch the list of NFT collections with downloaded and cached off chain data like token metadata and asset files.
   *
   * @param {string} chainName - The chain name eg: `eth-mainnet`.
   * @param {GetChainCollectionsQueryParamOpts} queryParamOpts
   *   - `pageSize`: Number of items per page. Omitting this parameter defaults to 100.
   *   - `pageNumber`: 0-indexed page number to begin pagination.
   *   - `noSpam`: If `true`, the suspected spam tokens are removed. Supports `eth-mainnet` and `matic-mainnet`.
   *
   */
  public async *getChainCollections(
    chainName: Chain,
    queryParamOpts?: GetChainCollectionsQueryParamOpts
  ): AsyncIterable<GoldRushResponse<ChainCollectionResponse>> {
    const endpoint = endpointGenerator(`${chainName}/nft/collections`, [
      {
        key: "page-size",
        value: queryParamOpts?.pageSize,
      },
      {
        key: "page-number",
        value: queryParamOpts?.pageNumber,
      },
      {
        key: "no-spam",
        value: queryParamOpts?.noSpam,
      },
    ]);

    const parseData = (data: GoldRushResponse<ChainCollectionResponse>) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((collectionItem) => ({
              ...collectionItem,
              last_scraped_at: collectionItem.last_scraped_at
                ? new Date(collectionItem.last_scraped_at)
                : null,
            }))
          : null;
      }

      return data;
    };

    for await (const data of paginateEndpoint<ChainCollectionResponse>(
      endpoint,
      this.execution,
      parseData,
      "pagination"
    )) {
      yield data;
    }
  }

  /**
   *
   * Commonly used to fetch the list of NFT collections with downloaded and cached off chain data like token metadata and asset files.
   *
   * @param {string} chainName - The chain name eg: `eth-mainnet`.
   * @param {GetChainCollectionsQueryParamOpts} queryParamOpts
   *   - `pageSize`: Number of items per page. Omitting this parameter defaults to 100.
   *   - `pageNumber`: 0-indexed page number to begin pagination.
   *   - `noSpam`: If `true`, the suspected spam tokens are removed. Supports `eth-mainnet` and `matic-mainnet`.
   *
   */
  public async getChainCollectionsByPage(
    chainName: Chain,
    queryParamOpts?: GetChainCollectionsQueryParamOpts
  ): Promise<GoldRushResponse<ChainCollectionResponse>> {
    const endpoint = endpointGenerator(`${chainName}/nft/collections`, [
      {
        key: "page-size",
        value: queryParamOpts?.pageSize,
      },
      {
        key: "page-number",
        value: queryParamOpts?.pageNumber,
      },
      {
        key: "no-spam",
        value: queryParamOpts?.noSpam,
      },
    ]);

    const parseData = (data: GoldRushResponse<ChainCollectionResponse>) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((collectionItem) => ({
              ...collectionItem,
              last_scraped_at: collectionItem.last_scraped_at
                ? new Date(collectionItem.last_scraped_at)
                : null,
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<ChainCollectionResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to render the NFTs (including ERC721 and ERC1155) held by an address.
   *
   * @param {string} chainName - The chain name eg: `eth-mainnet`.
   * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   * @param {GetNftsForAddressQueryParamOpts} queryParamOpts
   *   - `noSpam`: If `true`, the suspected spam tokens are removed. Supports `eth-mainnet` and `matic-mainnet`.
   *   - `noNftAssetMetadata`: If `true`, the response shape is limited to a list of collections and token ids, omitting metadata and asset information. Helpful for faster response times and wallets holding a large number of NFTs.
   *   - `withUncached`: By default, this endpoint only works on chains where we've cached the assets and the metadata. When set to `true`, the API will fetch metadata from upstream servers even if it's not cached - the downside being that the upstream server can block or rate limit the call and therefore resulting in time outs or slow response times on the Covalent side.
   *
   */
  public async getNftsForAddress(
    chainName: Chain,
    walletAddress: string,
    queryParamOpts?: GetNftsForAddressQueryParamOpts
  ): Promise<GoldRushResponse<NftAddressBalanceNftResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/address/${walletAddress}/balances_nft`,
      [
        {
          key: "no-spam",
          value: queryParamOpts?.noSpam,
        },
        {
          key: "no-nft-asset-metadata",
          value: queryParamOpts?.noNftAssetMetadata,
        },
        {
          key: "with-uncached",
          value: queryParamOpts?.withUncached,
        },
      ]
    );

    const parseData = (
      data: GoldRushResponse<NftAddressBalanceNftResponse>
    ) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((balanceItem) => ({
              ...balanceItem,
              balance: bigIntParser(balanceItem.balance),
              balance_24h: bigIntParser(balanceItem.balance_24h),
              nft_data: balanceItem.nft_data
                ? balanceItem.nft_data.map((nftItem) => ({
                    ...nftItem,
                    token_id: bigIntParser(nftItem.token_id),
                  }))
                : null,
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<NftAddressBalanceNftResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to get NFT token IDs with metadata from a collection. Useful for building NFT card displays.
   *
   * @param {string} chainName - The chain name eg: `eth-mainnet`.
   * @param {string} contractAddress - The requested contract address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   * @param {GetTokenIdsForContractWithMetadataQueryParamOpts} queryParamOpts
   *   - `noMetadata`: Omit metadata.
   *   - `pageSize`: Number of items per page. Omitting this parameter defaults to 100.
   *   - `pageNumber`: 0-indexed page number to begin pagination.
   *   - `traitsFilter`: Filters NFTs based on a specific trait. If this filter is used, the API will return all NFTs with the specified trait. Accepts comma-separated values, is case-sensitive, and requires proper URL encoding.
   *   - `valuesFilter`: Filters NFTs based on a specific trait value. If this filter is used, the API will return all NFTs with the specified trait value. If used with "traits-filter", only NFTs matching both filters will be returned. Accepts comma-separated values, is case-sensitive, and requires proper URL encoding.
   *   - `withUncached`: By default, this endpoint only works on chains where we've cached the assets and the metadata. When set to `true`, the API will fetch metadata from upstream servers even if it's not cached - the downside being that the upstream server can block or rate limit the call and therefore resulting in time outs or slow response times on the Covalent side.
   *
   */
  public async *getTokenIdsForContractWithMetadata(
    chainName: Chain,
    contractAddress: string,
    queryParamOpts?: GetTokenIdsForContractWithMetadataQueryParamOpts
  ): AsyncIterable<GoldRushResponse<NftMetadataResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/nft/${contractAddress}/metadata`,
      [
        {
          key: "no-metadata",
          value: queryParamOpts?.noMetadata,
        },
        {
          key: "page-size",
          value: queryParamOpts?.pageSize,
        },
        {
          key: "page-number",
          value: queryParamOpts?.pageNumber,
        },
        {
          key: "traits-filter",
          value: queryParamOpts?.traitsFilter,
        },
        {
          key: "values-filter",
          value: queryParamOpts?.valuesFilter,
        },
        {
          key: "with-uncached",
          value: queryParamOpts?.withUncached,
        },
      ]
    );

    const parseData = (data: GoldRushResponse<NftMetadataResponse>) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((tokenItem) => ({
              ...tokenItem,
              nft_data: {
                ...tokenItem.nft_data!,
                token_id: bigIntParser(tokenItem.nft_data?.token_id),
              },
            }))
          : null;
      }

      return data;
    };

    for await (const data of paginateEndpoint<NftMetadataResponse>(
      endpoint,
      this.execution,
      parseData,
      "pagination"
    )) {
      yield data;
    }
  }

  /**
   *
   * Commonly used to get NFT token IDs with metadata from a collection. Useful for building NFT card displays.
   *
   * @param {string} chainName - The chain name eg: `eth-mainnet`.
   * @param {string} contractAddress - The requested contract address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   * @param {GetTokenIdsForContractWithMetadataQueryParamOpts} queryParamOpts
   *   - `noMetadata`: Omit metadata.
   *   - `pageSize`: Number of items per page. Omitting this parameter defaults to 100.
   *   - `pageNumber`: 0-indexed page number to begin pagination.
   *   - `traitsFilter`: Filters NFTs based on a specific trait. If this filter is used, the API will return all NFTs with the specified trait. Accepts comma-separated values, is case-sensitive, and requires proper URL encoding.
   *   - `valuesFilter`: Filters NFTs based on a specific trait value. If this filter is used, the API will return all NFTs with the specified trait value. If used with "traits-filter", only NFTs matching both filters will be returned. Accepts comma-separated values, is case-sensitive, and requires proper URL encoding.
   *   - `withUncached`: By default, this endpoint only works on chains where we've cached the assets and the metadata. When set to `true`, the API will fetch metadata from upstream servers even if it's not cached - the downside being that the upstream server can block or rate limit the call and therefore resulting in time outs or slow response times on the Covalent side.
   *
   */
  public async getTokenIdsForContractWithMetadataByPage(
    chainName: Chain,
    contractAddress: string,
    queryParamOpts?: GetTokenIdsForContractWithMetadataQueryParamOpts
  ): Promise<GoldRushResponse<NftMetadataResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/nft/${contractAddress}/metadata`,
      [
        {
          key: "no-metadata",
          value: queryParamOpts?.noMetadata,
        },
        {
          key: "page-size",
          value: queryParamOpts?.pageSize,
        },
        {
          key: "page-number",
          value: queryParamOpts?.pageNumber,
        },
        {
          key: "traits-filter",
          value: queryParamOpts?.traitsFilter,
        },
        {
          key: "values-filter",
          value: queryParamOpts?.valuesFilter,
        },
        {
          key: "with-uncached",
          value: queryParamOpts?.withUncached,
        },
      ]
    );

    const parseData = (data: GoldRushResponse<NftMetadataResponse>) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((tokenItem) => ({
              ...tokenItem,
              nft_data: {
                ...tokenItem.nft_data!,
                token_id: bigIntParser(tokenItem.nft_data?.token_id),
              },
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<NftMetadataResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to get a single NFT metadata by token ID from a collection. Useful for building NFT card displays.
   *
   * @param {string} chainName - The chain name eg: `eth-mainnet`.
   * @param {string} contractAddress - The requested contract address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   * @param {string} tokenId - The requested token ID.
   * @param {GetNftMetadataForGivenTokenIdForContractQueryParamOpts} queryParamOpts
   *   - `noMetadata`: Omit metadata.
   *   - `withUncached`: By default, this endpoint only works on chains where we've cached the assets and the metadata. When set to `true`, the API will fetch metadata from upstream servers even if it's not cached - the downside being that the upstream server can block or rate limit the call and therefore resulting in time outs or slow response times on the Covalent side.
   *
   */
  public async getNftMetadataForGivenTokenIdForContract(
    chainName: Chain,
    contractAddress: string,
    tokenId: string,
    queryParamOpts?: GetNftMetadataForGivenTokenIdForContractQueryParamOpts
  ): Promise<GoldRushResponse<NftMetadataResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/nft/${contractAddress}/metadata/${tokenId}`,
      [
        {
          key: "no-metadata",
          value: queryParamOpts?.noMetadata,
        },
        {
          key: "with-uncached",
          value: queryParamOpts?.withUncached,
        },
      ]
    );

    const parseData = (data: GoldRushResponse<NftMetadataResponse>) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((tokenItem) => ({
              ...tokenItem,
              nft_data: {
                ...tokenItem.nft_data!,
                token_id: bigIntParser(tokenItem.nft_data?.token_id),
              },
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<NftMetadataResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to get all transactions of an NFT token. Useful for building a transaction history table or price chart.
   *
   * @param {string} chainName - The chain name eg: `eth-mainnet`.
   * @param {string} contractAddress - The requested contract address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   * @param {string} tokenId - The requested token ID.
   * @param {GetNftTransactionsForContractTokenIdQueryParamOpts} queryParamOpts
   *   - `noSpam`: If `true`, the suspected spam tokens are removed. Supports `eth-mainnet` and `matic-mainnet`.
   *
   */
  public async getNftTransactionsForContractTokenId(
    chainName: Chain,
    contractAddress: string,
    tokenId: string,
    queryParamOpts?: GetNftTransactionsForContractTokenIdQueryParamOpts
  ): Promise<GoldRushResponse<NftTransactionsResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/tokens/${contractAddress}/nft_transactions/${tokenId}`,
      [
        {
          key: "no-spam",
          value: queryParamOpts?.noSpam,
        },
      ]
    );

    const parseData = (data: GoldRushResponse<NftTransactionsResponse>) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((nftItem) => ({
              ...nftItem,
              nft_transactions: nftItem.nft_transactions
                ? nftItem.nft_transactions.map((txItem) => ({
                    ...txItem,
                    block_signed_at: txItem.block_signed_at
                      ? new Date(txItem.block_signed_at)
                      : null,
                    value: bigIntParser(txItem.value),
                    fees_paid: bigIntParser(txItem.fees_paid),
                    log_events: txItem.log_events
                      ? txItem.log_events.map((logItem) => ({
                          ...logItem,
                          block_signed_at: logItem.block_signed_at
                            ? new Date(logItem.block_signed_at)
                            : null,
                        }))
                      : null,
                  }))
                : null,
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<NftTransactionsResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to fetch and render the traits of a collection as seen in rarity calculators.
   *
   * @param {string} chainName - The chain name eg: `eth-mainnet`.
   * @param {string} collectionContract - The requested collection address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   *
   */
  public async getTraitsForCollection(
    chainName: Chain,
    collectionContract: string
  ): Promise<GoldRushResponse<NftCollectionTraitsResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/nft/${collectionContract}/traits`,
      []
    );

    const parseData = (data: GoldRushResponse<NftCollectionTraitsResponse>) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
      }

      return data;
    };

    return await this.execution.execute<NftCollectionTraitsResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to get the count of unique values for traits within an NFT collection.
   *
   * @param {string} chainName - The chain name eg: `eth-mainnet`.
   * @param {string} collectionContract - The requested collection address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   * @param {string} trait - The requested trait.
   *
   */
  public async getAttributesForTraitInCollection(
    chainName: Chain,
    collectionContract: string,
    trait: string
  ): Promise<GoldRushResponse<NftCollectionAttributesForTraitResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/nft/${collectionContract}/traits/${trait}/attributes`,
      []
    );

    const parseData = (
      data: GoldRushResponse<NftCollectionAttributesForTraitResponse>
    ) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
      }

      return data;
    };

    return await this.execution.execute<NftCollectionAttributesForTraitResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to calculate rarity scores for a collection based on its traits.
   *
   * @param {string} chainName - The chain name eg: `eth-mainnet`.
   * @param {string} collectionContract - The requested collection address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   *
   */
  public async getCollectionTraitsSummary(
    chainName: Chain,
    collectionContract: string
  ): Promise<GoldRushResponse<NftCollectionTraitsSummaryResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/nft/${collectionContract}/traits_summary`,
      []
    );

    const parseData = (
      data: GoldRushResponse<NftCollectionTraitsSummaryResponse>
    ) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
      }

      return data;
    };

    return await this.execution.execute<NftCollectionTraitsSummaryResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to render a price floor chart for an NFT collection.
   *
   * @param {string} chainName - The chain name eg: `eth-mainnet`.
   * @param {string} collectionAddress - The requested address.
   * @param {GetNftsForAddressQueryParamOpts} queryParamOpts
   *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
   *   - `days`: The number of days to return data for. Request up 365 days. Defaults to 30 days.
   *
   */
  public async getHistoricalFloorPricesForCollection(
    chainName: Chain,
    collectionAddress: string,
    queryParamOpts?: GetCollectionMarketDataQueryParamOpts
  ): Promise<GoldRushResponse<NftCollectionFloorPriceResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/nft_market/${collectionAddress}/floor_price`,
      [
        {
          key: "days",
          value: queryParamOpts?.days,
        },
        {
          key: "quote-currency",
          value: queryParamOpts?.quote_currency,
        },
      ]
    );

    const parseData = (
      data: GoldRushResponse<NftCollectionFloorPriceResponse>
    ) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((floorPriceItem) => ({
              ...floorPriceItem,
              date: floorPriceItem.date ? new Date(floorPriceItem.date) : null,
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<NftCollectionFloorPriceResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to build a time-series chart of the transaction volume of an NFT collection.
   *
   * @param {string} chainName - The chain name eg: `eth-mainnet`.
   * @param {string} collectionAddress - The requested address.
   * @param {GetNftsForAddressQueryParamOpts} queryParamOpts
   *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
   *   - `days`: The number of days to return data for. Request up 365 days. Defaults to 30 days.
   *
   */
  public async getHistoricalVolumeForCollection(
    chainName: Chain,
    collectionAddress: string,
    queryParamOpts?: GetCollectionMarketDataQueryParamOpts
  ): Promise<GoldRushResponse<NftCollectionVolumeResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/nft_market/${collectionAddress}/volume`,
      [
        {
          key: "days",
          value: queryParamOpts?.days,
        },
        {
          key: "quote-currency",
          value: queryParamOpts?.quote_currency,
        },
      ]
    );

    const parseData = (data: GoldRushResponse<NftCollectionVolumeResponse>) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((floorPriceItem) => ({
              ...floorPriceItem,
              date: floorPriceItem.date ? new Date(floorPriceItem.date) : null,
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<NftCollectionVolumeResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to build a time-series chart of the sales count of an NFT collection.
   *
   * @param {string} chainName - The chain name eg: `eth-mainnet`.
   * @param {string} collectionAddress - The requested address.
   * @param {GetNftsForAddressQueryParamOpts} queryParamOpts
   *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
   *   - `days`: The number of days to return data for. Request up 365 days. Defaults to 30 days.
   *
   */
  public async getHistoricalSalesCountForCollection(
    chainName: Chain,
    collectionAddress: string,
    queryParamOpts?: GetCollectionMarketDataQueryParamOpts
  ): Promise<GoldRushResponse<NftCollectionSalesCountResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/nft_market/${collectionAddress}/sale_count`,
      [
        {
          key: "days",
          value: queryParamOpts?.days,
        },
        {
          key: "quote-currency",
          value: queryParamOpts?.quote_currency,
        },
      ]
    );

    const parseData = (
      data: GoldRushResponse<NftCollectionSalesCountResponse>
    ) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((floorPriceItem) => ({
              ...floorPriceItem,
              date: floorPriceItem.date ? new Date(floorPriceItem.date) : null,
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<NftCollectionSalesCountResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to verify ownership of NFTs (including ERC-721 and ERC-1155) within a collection.
   *
   * @param {string} chainName - The chain name eg: `eth-mainnet`.
   * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   * @param {string} collectionContract - The requested collection address.
   * @param {CheckOwnershipInNftQueryParamOpts} queryParamOpts
   *   - `traitsFilter`: Filters NFTs based on a specific trait. If this filter is used, the API will return all NFTs with the specified trait. Must be used with "values-filter", is case-sensitive, and requires proper URL encoding.
   *   - `valuesFilter`: Filters NFTs based on a specific trait value. If this filter is used, the API will return all NFTs with the specified trait value. Must be used with "traits-filter", is case-sensitive, and requires proper URL encoding.
   *
   */
  public async checkOwnershipInNft(
    chainName: Chain,
    walletAddress: string,
    collectionContract: string,
    queryParamOpts?: CheckOwnershipInNftQueryParamOpts
  ): Promise<GoldRushResponse<NftOwnershipForCollectionResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/address/${walletAddress}/collection/${collectionContract}`,
      [
        {
          key: "traits-filter",
          value: queryParamOpts?.traitsFilter,
        },
        {
          key: "values-filter",
          value: queryParamOpts?.valuesFilter,
        },
      ]
    );

    const parseData = (
      data: GoldRushResponse<NftOwnershipForCollectionResponse>
    ) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((nftItem) => ({
              ...nftItem,
              balance: bigIntParser(nftItem.balance),
              balance_24h: bigIntParser(nftItem.balance_24h),
              token_id: bigIntParser(nftItem.token_id),
              nft_data: {
                ...nftItem.nft_data!,
                token_id: bigIntParser(nftItem.nft_data?.token_id),
              },
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<NftOwnershipForCollectionResponse>(
      endpoint,
      parseData
    );
  }

  /**
   *
   * Commonly used to verify ownership of a specific token (ERC-721 or ERC-1155) within a collection.
   *
   * @param {string} chainName - The chain name eg: `eth-mainnet`.
   * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   * @param {string} collectionContract - The requested collection address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
   * @param {string} tokenId - The requested token ID.
   *
   */
  public async checkOwnershipInNftForSpecificTokenId(
    chainName: Chain,
    walletAddress: string,
    collectionContract: string,
    tokenId: string
  ): Promise<GoldRushResponse<NftOwnershipForCollectionResponse>> {
    const endpoint = endpointGenerator(
      `${chainName}/address/${walletAddress}/collection/${collectionContract}/token/${tokenId}`,
      []
    );

    const parseData = (
      data: GoldRushResponse<NftOwnershipForCollectionResponse>
    ) => {
      if (data.data) {
        data.data.updated_at = data.data.updated_at
          ? new Date(data.data.updated_at)
          : null;
        data.data.items = data.data.items
          ? data.data.items.map((balanceItem) => ({
              ...balanceItem,
              balance: bigIntParser(balanceItem.balance),
              balance_24h: bigIntParser(balanceItem.balance_24h),
              token_id: bigIntParser(balanceItem.token_id),
              last_transfered_at: balanceItem.last_transfered_at
                ? new Date(balanceItem.last_transfered_at)
                : null,
            }))
          : null;
      }

      return data;
    };

    return await this.execution.execute<NftOwnershipForCollectionResponse>(
      endpoint,
      parseData
    );
  }
}
